/**
 * useApiQuery — Typed TanStack Query wrapper with standard error handling.
 * Uses @plerity/data-access for the underlying HTTP client.
 */

import {
  type ApiResponse,
  AUTH_ERROR_CODES,
  AuthError,
  type AxiosRequestConfig,
  createFetcher,
  isAuthError,
  type KeyTransformer,
  snakeToCamelKeys,
} from "@plerity/data-access";
import { type UseMutationOptions, type UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";

export { AuthError, isAuthError };

/**
 * Optional response-data transform applied AFTER envelope unwrap.
 *
 * - `undefined` / `false` (default): no transform — response.data.data is
 *   returned exactly as the backend emitted it. ALL pre-2026-05-26 callers
 *   rely on this — DO NOT change the default without a fleet-wide audit.
 * - `true`: apply `snakeToCamelKeys` recursively. Use when the backend wire
 *   format is snake_case and the consuming UI types are camelCase
 *   (governance is the first migration; see S08 audit
 *   agents/analysis/useapiquery_fetcher_parity_audit_2026-05-26.md).
 * - function: a custom transformer (e.g. camel→snake on outbound writes).
 *
 * Mirrors the `transform` knob on `createUnwrappedFetcher` so apps that
 * previously composed their own `services/api.ts` over the unwrapped
 * fetcher can migrate to the shared hook without losing semantics.
 */
export type TransformOption = boolean | KeyTransformer | undefined;

/** Apply the transform policy to an already-unwrapped payload. */
export function applyTransform<T>(payload: unknown, transform: TransformOption): T {
  if (!transform) {
    return payload as T;
  }
  const transformer: KeyTransformer = typeof transform === "function" ? transform : snakeToCamelKeys;
  return transformer(payload) as T;
}

/**
 * Optional runtime validator for the unwrapped response payload (Phase 6 of
 * the response-shape parity campaign — Layer 2: Zod fetch-boundary validation).
 *
 * Structurally typed as anything with a `parse(data: unknown): T` method —
 * every Zod schema (`z.object({...})`, schemas from `@plerity/contracts`)
 * satisfies this, so dashboard-shared needs NO direct `zod` dependency and the
 * fleet incurs no new coupling. Callers pass a contracts schema; we call
 * `.parse()` on the already-unwrapped, already-transformed payload.
 *
 * When a query opts in, shape drift between the backend `response_model` and
 * the UI's expected `<T>` surfaces as a `ResponseValidationError` attributed to
 * the request URL — at the network boundary — instead of as a
 * `Cannot read properties of undefined` crash deep in a component render.
 * This is the runtime complement to Layer 1 (OpenAPI codegen, which keeps the
 * STATIC `<T>` honest) and burns down the `apps-zod-response-validation`
 * adoption debt one query at a time.
 *
 * Default (`undefined`): no validation — current behavior, zero overhead.
 */
export interface ResponseValidator<T> {
  parse(data: unknown): T;
}

/**
 * Thrown when an opted-in response fails schema validation at the fetch
 * boundary. Carries the request `url` so the global ErrorBoundary / toast layer
 * can attribute the failure to a specific endpoint. The original validation
 * error (e.g. a `ZodError` with its `.issues`) is preserved on `cause`.
 */
export class ResponseValidationError extends Error {
  readonly url: string;
  /** The original validation error (e.g. a `ZodError` with its `.issues`). */
  readonly cause?: unknown;
  constructor(message: string, url: string, options?: { cause?: unknown }) {
    // Assign `cause` manually rather than via `super(message, { cause })` so the
    // class compiles under pre-ES2022 lib targets (some app tsconfigs do not
    // include the `ErrorOptions` overload).
    super(message);
    this.name = "ResponseValidationError";
    this.url = url;
    this.cause = options?.cause;
  }
}

/** Validate an unwrapped+transformed payload, re-throwing with URL attribution. */
export function validatePayload<T>(schema: ResponseValidator<T>, payload: unknown, url: string): T {
  try {
    return schema.parse(payload);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new ResponseValidationError(`Response shape validation failed for ${url}: ${detail}`, url, { cause: err });
  }
}

/**
 * Shared API client for all dashboard apps.
 *
 * In production, each app's Vite build bakes VITE_API_URL into the bundle
 * (e.g. "https://seo-api.animasmarketing.com"), pointing requests to the
 * Cloudflare Tunnel-backed FastAPI server. In local dev, the var is unset
 * and requests go to relative "/api" paths via the Vite dev proxy.
 */
const apiClient = createFetcher({
  baseURL: import.meta.env.VITE_API_URL || "",
  withCredentials: true,
});

/**
 * Resolve the error to throw from a non-success envelope. Returns an
 * `AuthError` when the envelope's `error_code` matches an auth failure
 * (so the global ErrorBoundary / toast layer can branch via `instanceof
 * AuthError`); otherwise a plain `Error` for app-level handling.
 */
function envelopeError(envelope: { error?: string; error_code?: string; message?: string }): Error {
  const message = envelope.error ?? envelope.message ?? "Request failed";
  const code = envelope.error_code;
  if (code && AUTH_ERROR_CODES.has(code)) {
    return new AuthError(message, code, 401);
  }
  return new Error(message);
}

/**
 * Unwrap a backend response body. The Plerity envelope contract is:
 *   - success path: backends MAY emit raw payload (no envelope) OR
 *     `{ success: true, data: T }`. Both shapes are accepted.
 *   - error path: backends emit `{ success: false, error?, error_code? }`
 *     via the shared `_shared/error_middleware.py` global handler.
 *
 * Envelope detection is by presence of the `success` key. Raw payloads that
 * happen to carry no `success` key are returned as-is — this matches the
 * `createUnwrappedFetcher.unwrap` policy in `@plerity/data-access`, which
 * is the SSoT for this contract. Governance was the first migration to
 * exercise the raw-success path (see S00 of the 2026-05-26 blitz);
 * envelope-emitting backends (ai, finance, crm, etc.) are unaffected.
 *
 * @throws Error / AuthError when the body is an error envelope.
 */
export function unwrapBody<T>(body: unknown): T {
  if (body != null && typeof body === "object" && "success" in body) {
    const env = body as { success: boolean; data?: unknown; error?: string; error_code?: string; message?: string };
    if (env.success === false) {
      throw envelopeError(env);
    }
    // success === true (or any truthy non-false) → unwrap `.data` if present
    if ("data" in env) {
      return env.data as T;
    }
    // success: true with no data field → treat the envelope itself as payload.
    // Rare but valid; matches pre-2026-05-26 behavior of returning `undefined`.
    return undefined as T;
  }
  // No `success` key → raw payload (governance, any pre-envelope backend).
  return body as T;
}

/** GET query with typed response */
export function useApiQuery<T>(
  queryKey: readonly unknown[],
  url: string,
  options?: Omit<UseQueryOptions<T>, "queryKey" | "queryFn"> & {
    axiosConfig?: AxiosRequestConfig;
    /** See {@link TransformOption}. Default: undefined (no transform). */
    transform?: TransformOption;
    /**
     * Optional Zod (or `{ parse }`-shaped) schema validating the unwrapped,
     * transformed payload at the fetch boundary. See {@link ResponseValidator}.
     * Default: undefined (no validation). On failure throws
     * {@link ResponseValidationError} attributed to `url`.
     */
    schema?: ResponseValidator<T>;
  }
) {
  const { axiosConfig, transform, schema, ...queryOptions } = options ?? {};

  return useQuery<T>({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<T> | T>(url, axiosConfig);
      const payload = unwrapBody<T>(response.data);
      const transformed = applyTransform<T>(payload, transform);
      return schema ? validatePayload<T>(schema, transformed, url) : transformed;
    },
    ...queryOptions,
  });
}

/** POST/PUT/DELETE mutation with typed request/response */
export function useApiMutation<TData, TVariables>(
  url: string,
  method: "post" | "put" | "patch" | "delete" = "post",
  options?: UseMutationOptions<TData, Error, TVariables> & {
    /** See {@link TransformOption}. Default: undefined (no transform). */
    transform?: TransformOption;
    /**
     * Optional schema validating the unwrapped, transformed response payload at
     * the fetch boundary. See {@link ResponseValidator}. Default: undefined.
     */
    schema?: ResponseValidator<TData>;
  }
) {
  const { transform, schema, ...mutationOptions } = options ?? {};

  return useMutation<TData, Error, TVariables>({
    mutationFn: async (variables) => {
      const response = await apiClient[method]<ApiResponse<TData> | TData>(url, variables);
      const payload = unwrapBody<TData>(response.data);
      const transformed = applyTransform<TData>(payload, transform);
      return schema ? validatePayload<TData>(schema, transformed, url) : transformed;
    },
    ...mutationOptions,
  });
}

export { apiClient };
